<template>
  <div class="wind-overhead">
    <h3>上空の風</h3>

    <div class="tabs-container">
      <div class="tab-buttons" v-if="upperWeathers.length > 1">
        <button
          v-for="(point, i) in upperWeathers"
          :key="point.point_no"
          class="tab-btn"
          :class="{ active: activeIndex === i }"
          @click="activeIndex = i"
        >
          {{ point.point_name }}
        </button>
      </div>

      <div class="tab-contents">
        <div class="tab-pane active">
          <div class="overview-teble-head">
            {{ currentPoint?.point_name }}
          </div>

          <div class="table-weather-day" style="width: 97%">
            <ul class="header-column">
              <li>高度</li>
              <li v-for="h in heights" :key="h">
                <span>{{ h }}<span class="unit">m</span></span>
              </li>
            </ul>

            <div class="weather-hour" ref="scrollContainer">
              <WindHourColumn
                v-for="(col, i) in windColumns"
                :key="col.fullTime"
                :hour="col.hour"
                :winds="col.winds"
                :scrollOffset="i < 2"
                :isCurrent="col.isCurrent"
                :isPast="new Date(col.fullTime).getTime() < pastBaseTime"
              />
            </div>
          </div>
          <ul class="usage-note-overview">
            <li><span></span>注意以上</li>
            <li><span></span>警戒以上</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import WindHourColumn from './WindHourColumn.vue'
import type { UpperWeatherPoint } from '@/types/weatherOverview'
import WindIcon from '@/composables/WindIcon'

type WindCell = {
  windSpeed: number
  level?: 'warning' | 'danger'
  icon: string
  temp?: number
}
type UpperWind = UpperWeatherPoint['winds'][number]

const props = defineProps<{
  upperWeathers: UpperWeatherPoint[]
  highlightTime: number
}>()

const activeIndex = ref(0)
const currentPoint = computed(() => props.upperWeathers[activeIndex.value])
const heights = [200, 150, 100, 50, 10]
const scrollContainer = ref<HTMLElement | null>(null)

/* =========================
   時間キー統一
========================= */
const toSlotKey = (date: Date) => {
  const d = new Date(date)
  d.setMinutes(d.getMinutes() < 30 ? 0 : 30, 0, 0)
  return d.toISOString()
}

const highlightSlot = computed(() => {
  if (!currentPoint.value) return ''

  // 出発時刻
  const base = new Date(props.highlightTime)

  // point_pass_time（例 "01:30"）
  const pass = currentPoint.value.point_pass_time ?? '00:00'

  const parts = pass.split(':')
  const h = Number(parts[0] ?? 0)
  const m = Number(parts[1] ?? 0)

  // 出発 + 通過時間
  const actual = new Date(base)
  actual.setMinutes(actual.getMinutes() + h * 60 + m)

  // 30分切り上げ
  return toSlotKey(ceilTo30Minutes(actual))
})

const ceilTo30Minutes = (date: Date) => {
  const d = new Date(date)
  const m = d.getMinutes()

  if (m === 0 || m === 30) return d

  if (m < 30) {
    d.setMinutes(30, 0, 0)
  } else {
    d.setHours(d.getHours() + 1)
    d.setMinutes(0, 0, 0)
  }

  return d
}

/* =========================
   30分正規化
========================= */
const normalizeWinds = (winds: UpperWeatherPoint['winds']) => {
  const map = new Map<string, UpperWind>()

  winds.forEach((w) => {
    const key = toSlotKey(new Date(w.forecast_time))
    if (!map.has(key)) {
      map.set(key, w)
    }
  })

  return Array.from(map.values())
}

/* =========================
   列データ生成
========================= */
const windColumns = computed(() => {
  if (!currentPoint.value) return []

  const normalized = normalizeWinds(currentPoint.value.winds)

  return normalized.map((w) => {
    const slot = toSlotKey(new Date(w.forecast_time))
    const d = new Date(w.forecast_time)

    return {
      fullTime: slot,
      hour: `${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}`,
      isCurrent: slot === highlightSlot.value,
      winds: heights.map((h) => buildHeightData(w, h)),
    }
  })
})

type HeightKey = `${(typeof heights)[number]}m`
const buildHeightData = (w: UpperWind, h: (typeof heights)[number]): WindCell => {
  const key = `${h}m` as HeightKey

  const speed = w[`wind_speed_${key}` as keyof UpperWind] as number
  const alert = w[`wind_alert_${key}` as keyof UpperWind] as number
  const dir = w[`wind_dir_${key}` as keyof UpperWind] as number

  return {
    windSpeed: speed,
    level: toLevel(alert),
    icon: WindIcon.getIconUrl(dir, speed),
    temp: h === 10 ? w.temp : undefined,
  }
}

const floorSlot = computed(() => {
  const d = new Date(props.highlightTime)

  d.setMinutes(d.getMinutes() - 30)

  return toSlotKey(d)
})
const floorIndex = computed(() =>
  windColumns.value.findIndex((c) => c.fullTime === floorSlot.value),
)

const currentIndex = computed(() => windColumns.value.findIndex((c) => c.isCurrent))

const pastBaseTime = computed(() => {
  const d = new Date(props.highlightTime)
  d.setMinutes(d.getMinutes() - 30)

  return d.getTime()
})

/* =========================
   🔥 初期スクロール制御
========================= */
watch(
  [windColumns, currentIndex, activeIndex],
  async () => {
    await nextTick()

    const container = scrollContainer.value
    if (!container) return

    const columns = container.querySelectorAll<HTMLElement>('.data-column')
    if (!columns.length) return

    const index = floorIndex.value
    if (index < 0) return

    const targetColumn = columns[index]
    if (!targetColumn) return

    const heightColumn = container.querySelector<HTMLElement>('.header-column')
    const offset = heightColumn ? heightColumn.offsetWidth : 0

    container.scrollTo({
      left: targetColumn.offsetLeft - offset,
      behavior: 'auto',
    })
  },
  { immediate: true },
)

watch(
  () => props.upperWeathers,
  () => {
    activeIndex.value = 0
  },
  { immediate: true },
)

function toLevel(alert: number): 'warning' | 'danger' | undefined {
  if (alert === 2) return 'danger'
  if (alert === 1) return 'warning'
  return undefined
}
</script>
